import {
  ChangeEventHandler,
  ComponentProps,
  FC,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './styles.module.css';
import { CompanyFormData } from '../../../../types/companies';
import { useGetCompanyTypesQuery } from '../../../../api/companyApi';
import {
  useGetBusinessTypesQuery,
  useGetPrefectureQuery,
  useGetWasteItemsQuery,
} from '../../../../api/otherApi';
import { FormProvider, useForm } from 'react-hook-form';
import CheckBoxGroup, {
  CheckBoxGroupOnChangeProps,
  ValueObj,
} from '../../../../components/common/CheckBoxGroup';
import Select, { Option } from '../../../../components/common/Select';
import {
  timeOutOfRangeErrorMessage,
  halfWidth,
  halfWidthPhoneNumber,
  invalidTimeErrorMessage,
  max,
  required,
} from '../../../../message';
import { logger } from '../../../../services/logger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import FormItem from '../../../../components/common/FormItem';
import Input from '../../../../components/common/Input';
import classNames from 'classnames';
import {
  correctTimeMinutes,
  validateTimeMinutes,
} from '../../../../validators/time';
import Label from '../../../../components/common/Label';
import Button from '../../../../components/common/Button';
import { validateHalfWidthPhoneNumber } from '../../../../validators/halfWidth';
import { ErrorMessage } from '@hookform/error-message';
import Caption, {
  CaptionsUnderForm,
} from '../../../../components/common/Caption';
import { parse } from 'date-fns';

export type CompanyFormDataProviderProps = {
  formData: CompanyFormData;
  type: 'create' | 'edit';
  onSubmit: (data: CompanyFormData) => void;
};
const context = createContext<CompanyFormDataProviderProps | null>(null);
export const CompanyFormDataProvider = context.Provider;

const EditSideBar: FC = () => {
  const companyFormDataProps = useContext(context);
  if (!companyFormDataProps) {
    return <></>;
  }
  return <EditCompanyForm {...companyFormDataProps} />;
};

const labels: Record<keyof CompanyFormData, string> = {
  businessTypeId: '業種',
  companyName: '会社名',
  postCode: '郵便番号',
  prefectureId: '都道府県',
  address1: '市区町村',
  address2: '丁目・番地・号',
  address3: '建物名・部屋番号',
  phoneNumber: '電話番号',
  companyTypeId: '会社区分',
  businessHoursStartAt: '営業時間(開始)',
  businessHoursEndAt: '営業時間(終了)',
  companyWasteItemIds: '処理区分',
};
const setValueOpt = {
  shouldTouch: true,
  shouldDirty: true,
  shouldValidate: true,
};
type TypedFormItemProps = ComponentProps<typeof FormItem<CompanyFormData>>;
const TypedFormItem = (props: TypedFormItemProps) => (
  <div className={styles.formItem}>
    <FormItem<CompanyFormData> {...props} />
  </div>
);
const EditCompanyForm = ({
  formData,
  type,
  onSubmit,
}: CompanyFormDataProviderProps) => {
  const form = useForm({
    defaultValues: formData,
    mode: 'onBlur',
  });
  const { register, setValue, watch, handleSubmit, getFieldState, trigger } =
    form;
  useEffect(() => {
    form.reset(formData);
  }, [formData]);
  const submitHandler = handleSubmit(onSubmit, (e) => logger.error(e));

  const [isOpened, setIsOpened] = useState(false);
  useEffect(() => {
    setIsOpened(true);
  }, [formData]);
  const closeForm = () => setIsOpened(false);

  // 会社区分ハンドラセットアップ
  const { data: companyTypes = [] } = useGetCompanyTypesQuery();
  const companyTypeOptions: Option[] = companyTypes.map((ct) => ({
    label: ct.companyType,
    value: `${ct.companyTypeId}`,
  }));
  const onChangeCompanyType: ChangeEventHandler<HTMLSelectElement> = (e) => {
    setValue('companyTypeId', Number(e.target.value), setValueOpt);
  };
  register('companyTypeId', {
    required: required(labels.companyTypeId),
  });

  // 都道府県ハンドラセットアップ
  const { data: prefectures = [] } = useGetPrefectureQuery();
  const prefecturesOptions: Option[] = prefectures.map((p) => ({
    label: p.prefecture,
    value: `${p.prefectureId}`,
  }));
  const onChangePrefecture: ChangeEventHandler<HTMLSelectElement> = (e) => {
    setValue('prefectureId', Number(e.target.value), setValueOpt);
  };
  register('prefectureId', {
    required: required(labels.prefectureId),
  });

  // 業種ハンドラセットアップ
  const companyTypeId = watch('companyTypeId');
  const showsBusinessTypes =
    companyTypeId !== null &&
    companyTypes
      .filter((ct) => ct.companyType === '顧客')
      .map((ct) => ct.companyTypeId)
      .includes(companyTypeId);
  const { data: businessTypes = [] } = useGetBusinessTypesQuery();
  const businessTypesOptions: Option[] = businessTypes.map((p) => ({
    label: p.businessType,
    value: `${p.businessTypeId}`,
  }));
  const onChangeBusinessTypes: ChangeEventHandler<HTMLSelectElement> = (e) => {
    setValue('businessTypeId', Number(e.target.value), setValueOpt);
  };
  register('businessTypeId', {
    required: companyTypeId === /* 顧客 */ 2 && required(labels.businessTypeId),
  });

  // 処理区分ハンドラセットアップ
  const showsWasteItems =
    companyTypeId !== null &&
    companyTypes
      .filter(
        (ct) =>
          ct.companyType === '家電処分' ||
          ct.companyType === '処分事業者' ||
          ct.companyType === '産廃事業者'
      )
      .map((ct) => ct.companyTypeId)
      .includes(companyTypeId);
  const { data: wasteItems } = useGetWasteItemsQuery();
  const [wasteItemsValues, setWasteItemsValues] = useState<ValueObj[]>([]);
  useEffect(() => {
    setWasteItemsValues(
      (wasteItems || []).map((item, i) => ({
        label: `${i + 1}.${item.wasteItem}`,
        value: `${item.wasteItemId}`,
        checked: (formData.companyWasteItemIds ?? []).includes(
          item.wasteItemId
        ),
      }))
    );
  }, [wasteItems, formData]);
  const onChangeWasteItems = ({
    values,
    selectedValues,
  }: CheckBoxGroupOnChangeProps) => {
    setWasteItemsValues(values);
    setValue('companyWasteItemIds', selectedValues.map(Number), setValueOpt);
  };
  register('companyWasteItemIds', {
    validate: {
      required(value) {
        return (
          !showsWasteItems ||
          !value ||
          value.length !== 0 ||
          required(labels.companyWasteItemIds)
        );
      },
    },
  });

  const startTimeValidation = () => {
    return (
      form.getValues().businessHoursStartAt === '' ||
      form.getValues().businessHoursEndAt === '' ||
      parse(form.getValues().businessHoursStartAt, 'HH:mm', new Date()) <
        parse(form.getValues().businessHoursEndAt, 'HH:mm', new Date())
    );
  };

  const getVariant = (key: keyof CompanyFormData) => {
    if (
      key === 'businessHoursEndAt' &&
      getFieldState('businessHoursStartAt')?.error?.type === 'dateValidation'
    )
      return ['fit' as const, 'error' as const];
    return getFieldState(key)?.error
      ? ['fit' as const, 'error' as const]
      : ['fit' as const];
  };

  const formRef = useRef<HTMLFormElement | null>(null);

  return (
    <div
      className={classNames(
        styles.sidebar,
        isOpened ? styles.open : styles.close
      )}
    >
      <h4 className={styles.head}>
        {`会社${type === 'create' ? '登録' : '編集'}`}
        <button onClick={closeForm} className={styles.closer}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </h4>
      <FormProvider {...form}>
        <form onSubmit={submitHandler} className={styles.form} ref={formRef}>
          <TypedFormItem label={labels.companyName} name="companyName">
            <div className={styles.input}>
              <Input
                {...register('companyName', {
                  required: required(labels.companyName),
                  validate: {
                    max: (value) =>
                      value.length <= 255 || max(labels.companyName, 255),
                  },
                })}
                variants={getVariant('companyName')}
              />
            </div>
          </TypedFormItem>
          <div className={styles.subHead}>住所</div>
          <TypedFormItem label={labels.postCode} name="postCode">
            <div className={styles.input}>
              <Input
                {...register('postCode', {
                  required: required(labels.postCode),
                  validate: {
                    max: (value) =>
                      value.length <= 255 || max(labels.postCode, 255),
                    halfWidthPhoneNumber: (value) =>
                      !value ||
                      validateHalfWidthPhoneNumber(value) ||
                      halfWidthPhoneNumber(labels.postCode),
                  },
                })}
                variants={getVariant('postCode')}
              />
            </div>
          </TypedFormItem>
          <TypedFormItem label={labels.prefectureId} name="prefectureId">
            <div className={styles.input}>
              <Select
                options={prefecturesOptions}
                value={`${watch('prefectureId')}`}
                onChange={onChangePrefecture}
                variants={getVariant('prefectureId')}
              />
            </div>
          </TypedFormItem>
          <TypedFormItem label={labels.address1} name="address1">
            <div className={styles.input}>
              <Input
                {...register('address1', {
                  required: required(labels.address1),
                  validate: {
                    max: (value) =>
                      value.length <= 255 || max(labels.address1, 255),
                  },
                })}
                variants={getVariant('address1')}
              />
            </div>
          </TypedFormItem>
          <TypedFormItem label={labels.address2} name="address2">
            <div className={styles.input}>
              <Input
                {...register('address2', {
                  required: required(labels.address2),
                  validate: {
                    max: (value) =>
                      value.length <= 255 || max(labels.address2, 255),
                  },
                })}
                variants={getVariant('address2')}
              />
            </div>
          </TypedFormItem>
          <TypedFormItem label={labels.address3} name="address3" optional>
            <div className={styles.input}>
              <Input
                {...register('address3', {
                  validate: {
                    max: (value) =>
                      !value ||
                      value.length <= 255 ||
                      max(labels.address3, 255),
                  },
                })}
                variants={getVariant('address3')}
              />
            </div>
          </TypedFormItem>
          <TypedFormItem label={labels.phoneNumber} name="phoneNumber">
            <div className={styles.input}>
              <Input
                {...register('phoneNumber', {
                  required: required(labels.phoneNumber),
                  validate: {
                    max: (value) =>
                      value.length <= 255 || max(labels.phoneNumber, 255),
                    halfWidthPhoneNumber: (value) =>
                      validateHalfWidthPhoneNumber(value) ||
                      halfWidthPhoneNumber(labels.phoneNumber),
                  },
                })}
                variants={getVariant('phoneNumber')}
              />
            </div>
          </TypedFormItem>
          <TypedFormItem label={labels.companyTypeId} name="companyTypeId">
            <div className={styles.input}>
              <Select
                options={companyTypeOptions}
                value={`${companyTypeId}`}
                onChange={onChangeCompanyType}
                variants={getVariant('companyTypeId')}
              />
            </div>
          </TypedFormItem>
          {showsBusinessTypes && (
            <TypedFormItem label={labels.businessTypeId} name="businessTypeId">
              <div className={styles.input}>
                <Select
                  options={businessTypesOptions}
                  value={`${watch('businessTypeId')}`}
                  onChange={onChangeBusinessTypes}
                  variants={getVariant('businessTypeId')}
                />
              </div>
            </TypedFormItem>
          )}
          <TypedFormItem
            label="営業時間"
            name={['businessHoursStartAt', 'businessHoursEndAt']}
          >
            <div className={styles.businessHours}>
              <div className={styles.businessHourInput}>
                <Input
                  {...register('businessHoursStartAt', {
                    required: required(labels.businessHoursStartAt),
                    validate: {
                      pattern: (v) =>
                        v === '' ||
                        !v.match(/[０-９ ：]/) ||
                        halfWidth(labels.businessHoursStartAt),
                      time: (v) =>
                        validateTimeMinutes(v) || invalidTimeErrorMessage,
                      dateValidation: () =>
                        startTimeValidation() || timeOutOfRangeErrorMessage,
                    },
                    onBlur: (e) => {
                      setValue(
                        'businessHoursStartAt',
                        correctTimeMinutes(e.target.value),
                        { shouldValidate: true }
                      );
                      trigger(['businessHoursStartAt', 'businessHoursEndAt']);
                    },
                  })}
                  variants={getVariant('businessHoursStartAt')}
                />
              </div>
              <span className={styles.tilda}>~</span>
              <div className={styles.businessHourInput}>
                <Input
                  {...register('businessHoursEndAt', {
                    required: required(labels.businessHoursEndAt),
                    validate: {
                      pattern: (v) =>
                        v === '' ||
                        !v.match(/[０-９ ：]/) ||
                        halfWidth(labels.businessHoursEndAt),
                      time: (v) =>
                        validateTimeMinutes(v) || invalidTimeErrorMessage,
                    },
                    onBlur: (e) => {
                      setValue(
                        'businessHoursEndAt',
                        correctTimeMinutes(e.target.value),
                        { shouldValidate: true }
                      );
                      trigger(['businessHoursStartAt', 'businessHoursEndAt']);
                    },
                  })}
                  variants={getVariant('businessHoursEndAt')}
                />
              </div>
            </div>
          </TypedFormItem>
          {showsWasteItems && (
            <>
              <Label>{labels.companyWasteItemIds}</Label>
              <div className={styles.input}>
                <CheckBoxGroup
                  values={wasteItemsValues}
                  isVertical={true}
                  {...register('companyWasteItemIds', {
                    required: required(labels.companyWasteItemIds),
                  })}
                  onChange={onChangeWasteItems}
                  variants={
                    getFieldState('companyWasteItemIds').error && [
                      'error' as const,
                    ]
                  }
                />
                {/* TypedFormItemを使用するとフォーム選択時、先頭選択肢を自動的に選択される不具合が出るため、エラーキャプションを独自で設定 */}
                <CaptionsUnderForm>
                  <ErrorMessage
                    name={'companyWasteItemIds'}
                    render={(message) => (
                      <Caption variants={['error']}>{message.message}</Caption>
                    )}
                  ></ErrorMessage>
                </CaptionsUnderForm>
              </div>
            </>
          )}

          <div className={styles.controls}>
            <div className={styles.controlButton}>
              <Button
                type="button"
                variants={['fit']}
                onClick={() => formRef.current?.requestSubmit()}
              >
                確定
              </Button>
            </div>
            <div className={styles.controlButton}>
              <Button
                type="button"
                variants={['fit', 'cancel']}
                onClick={closeForm}
              >
                キャンセル
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default EditSideBar;
